/* 배경 지식. 호스트 모델이 풀이 추론에 들어가기 전에 알고 있어야 하는 것(사용자 지시 2026-10-21): 수능 수학의 형식 규칙과 유사 기출 참고.
 * 기출 원문은 참고 자료일 뿐이다. 유형을 확정하거나 정답을 주입하지 않고, 출처(연도, 번호)와 정답은 호스트에게 주지 않는다(사용자 규칙 2026-10-14) */
import { similarityPercent } from './measure';

/** 수능 수학 형식. 문항 제작과 풀이가 지키는 틀 */
export const CSAT_FORMAT_KNOWLEDGE = [
  '## 수능 수학 형식(배경 지식)',
  '- 한 회차는 30문항이다. 공통(수학Ⅰ, 수학Ⅱ) 1~22번, 선택(확률과 통계, 미적분, 기하 중 하나) 23~30번. 객관식은 ①~⑤ 다섯 선지이고 단답형은 0 이상 999 이하의 정수를 답한다.',
  '- 배점은 2점, 3점, 4점이다. 4점 문항 중 끝 번호(15, 21, 22, 28, 29, 30번 근처)가 어렵고, 조건 (가), (나)로 함수나 도형을 하나로 확정한 뒤 값을 구하는 꼴이 많다.',
  '- 발문은 「~를 구하시오.」(단답형), 「~의 값은?」(객관식) 으로 끝난다. 조건은 (가), (나), (다)로 적고, 그림이 필요한 문항은 좌표평면 위에 곡선, 점, 선, 도형으로 그린다. 그림은 축의 비율을 지키지 않아도 된다.',
  '- 풀이는 두 길로 쓴다. 정석 풀이는 개념 순서(도함수를 구한다, 극값을 분석한다, 조건을 연립한다)로 끝까지 가고, 실전 풀이는 시험장에서 쓰는 빠른 길(공식, 대칭, 특수값)이다. 두 풀이의 답은 같아야 한다.',
  '- 계산한 값(좌표, 길이, 넓이, 기울기)은 그림에서 도구로 다시 재서 답과 대조한다. 잰 값과 답이 다르면 풀이가 틀린 것이다.',
].join('\n');

interface ArchiveItem { id: string; year: number; subject: string; number: number; text: string }
let cache: Promise<ArchiveItem[]> | null = null;
let tagCache: Promise<{ rules: [string, string][]; tags: Record<string, string[]> }> | null = null;

/** 기술 태그(scripts/archive/build-tags.mjs 가 낱말 규칙으로 만든 data/archive/tags.json). 문항에도 같은 규칙을 적용해 태그 일치로 고른다(구조 유사도의 근사) */
export function loadTags(): Promise<{ rules: [string, string][]; tags: Record<string, string[]> }> {
  if (!tagCache) {
    tagCache = fetch('/archive/tags.json', { cache: 'force-cache' })
      .then((r) => (r.ok ? r.json() : { rules: [], tags: {} }))
      .then((d: { rules?: [string, string][]; tags?: Record<string, string[]> }) => ({ rules: Array.isArray(d?.rules) ? d.rules : [], tags: d?.tags ?? {} }))
      .catch(() => ({ rules: [], tags: {} }));
  }
  return tagCache;
}
export function tagsOf(text: string, rules: [string, string][]): string[] {
  const out: string[] = [];
  for (const [tag, source] of rules) { try { if (new RegExp(source).test(text)) out.push(tag); } catch { /* 규칙 오류는 건너뜀 */ } }
  return out;
}

/** 기출 색인(Go 서버가 /archive/index.json 으로 낸다). 한 번만 받는다. 못 받으면 빈 목록 */
export function loadArchive(): Promise<ArchiveItem[]> {
  if (!cache) {
    cache = fetch('/archive/index.json', { cache: 'force-cache' })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d: { items?: ArchiveItem[] }) => (Array.isArray(d?.items) ? d.items : []))
      .catch(() => []);
  }
  return cache;
}

/** 문항과 닮은 기출 k개의 원문(OCR). 같은 문항(유사도 95 이상)은 뺀다. 출처와 정답은 싣지 않는다 */
export async function similarPastItems(problem: string, k = 3): Promise<{ text: string; similarity: number; tags: string[] }[]> {
  const [items, tagData] = await Promise.all([loadArchive(), loadTags()]);
  const mine = new Set(tagsOf(problem, tagData.rules).filter((t) => t !== '객관식' && t !== '단답형'));
  const scored = items
    .filter((it) => typeof it.text === 'string' && it.text.length > 40)
    .map((it) => {
      const theirs = (tagData.tags[it.id] ?? []).filter((t) => t !== '객관식' && t !== '단답형');
      const both = theirs.filter((t) => mine.has(t)).length;
      const union = new Set([...theirs, ...mine]).size;
      const structural = union ? Math.round((100 * both) / union) : 0;
      const surface = similarityPercent(problem, it.text);
      // 구조(태그) 유사도가 주, 표면(bigram) 유사도는 동점 가르기(Learning by Analogy: 표면보다 구조)
      return { text: it.text, similarity: structural * 10 + surface, tags: theirs, surface };
    })
    .filter((it) => it.surface < 95 && it.similarity > 0)
    .sort((a, b) => b.similarity - a.similarity)
    .slice(0, k);
  return scored.map((it) => ({ text: it.text, similarity: it.similarity, tags: it.tags }));
}

/* 경험 저장소(worker/experience.ts 가 쓰는 JSON Lines, Go 가 /experience/ 로 낸다). 세션을 넘어 레시피, 실패, 선호가 쌓이고 다음 추론 전에 여기로 들어온다 */
interface RunRecord { at: string; hash: string; acceptance: string; reason: string; tags: string[]; problem: string; steps: string; quick: string; declared: string[][]; calls: string[]; relations: { kind: string; a: string; b: string; c: string | null; verdict: string }[]; errors: string[] }
interface NoteRecord { at: string; note: string; tags: string[]; scope: string }

async function readJsonl<T>(name: string): Promise<T[]> {
  try {
    const r = await fetch(`/experience/${name}`, { cache: 'no-store' });
    if (!r.ok) return [];
    return (await r.text()).split('\n').filter(Boolean).map((l) => { try { return JSON.parse(l) as T; } catch { return null; } }).filter((v): v is T => v !== null);
  } catch { return []; }
}

const jaccard = (a: string[], b: Set<string>): number => { const both = a.filter((t) => b.has(t)).length; const union = new Set([...a, ...b]).size; return union ? both / union : 0; };
const STRUCTURAL = (t: string): boolean => t !== '객관식' && t !== '단답형';

/** 이전 경험 가운데 이 문항과 태그가 겹치는 것. 레시피(성립)와 실패(불성립, 반려)를 나누고, 같은 문항(hash)은 최신 한 건만 */
export async function similarExperiences(problem: string, k = 3): Promise<{ recipes: RunRecord[]; failures: RunRecord[]; notes: NoteRecord[]; total: number }> {
  const [runs, notes, tagData] = await Promise.all([readJsonl<RunRecord>('runs.jsonl'), readJsonl<NoteRecord>('notes.jsonl'), loadTags()]);
  const mine = new Set(tagsOf(problem, tagData.rules).filter(STRUCTURAL));
  const latest = new Map<string, RunRecord>();
  for (const r of runs) latest.set(r.hash, r);
  const scored = [...latest.values()].map((r) => ({ r, score: jaccard((r.tags ?? []).filter(STRUCTURAL), mine) })).filter((x) => x.score > 0).sort((a, b) => b.score - a.score || b.r.at.localeCompare(a.r.at));
  const recipes = scored.filter((x) => x.r.acceptance === '성립').slice(0, k).map((x) => x.r);
  const failures = scored.filter((x) => x.r.acceptance !== '성립').slice(0, k).map((x) => x.r);
  const picked = notes.filter((n) => n.scope === 'global' || (n.tags ?? []).some((t) => mine.has(t)));
  return { recipes, failures, notes: picked, total: runs.length };
}

const clip = (text: string, n: number): string => text.replace(/\s+/g, ' ').trim().slice(0, n);

function experienceText(ex: { recipes: RunRecord[]; failures: RunRecord[]; notes: NoteRecord[] }): string {
  const out: string[] = [];
  if (ex.notes.length) out.push(['## 사용자 선호(사용자가 앞선 대화에서 말한 것. 따른다)', ...ex.notes.map((n, i) => `${i + 1}. ${n.note}${n.scope === 'global' ? '' : ` (태그 ${n.tags.join(', ')})`}`)].join('\n'));
  if (ex.recipes.length) out.push(['## 이전 경험 레시피(태그가 겹치는 성립 문항. 그림 선언과 호출 순서를 참고한다. 문항은 다르니 값은 베끼지 않는다)', ...ex.recipes.map((r, i) => {
    const decl = (r.declared ?? []).filter((d) => d.length).map((d, j) => `${j + 1}:[${d.join(', ')}]`).join(' ');
    return `${i + 1}. [${r.tags.join(', ')}] 문항: ${clip(r.problem, 200)}\n   그림 선언: ${decl || '없음'}\n   호출: ${(r.calls ?? []).slice(0, 10).map((c) => clip(c, 90)).join(' | ') || '없음'}\n   관계: ${(r.relations ?? []).map((rel) => `${rel.kind}(${rel.a},${rel.b}${rel.c ? `,${rel.c}` : ''})`).join(' ') || '없음'}`;
  })].join('\n'));
  if (ex.failures.length) out.push(['## 이전 실패(같은 길을 피한다)', ...ex.failures.map((r, i) => `${i + 1}. [${r.tags.join(', ')}] ${r.acceptance}: ${clip(r.reason, 160)}${(r.errors ?? []).length ? ` / 오류: ${clip(r.errors[0], 120)}` : ''}`)].join('\n'));
  return out.join('\n\n');
}

/** 풀이 작문 지시문에 붙는 배경 지식 절. 형식 규칙 + 유사 기출 참고 + 이전 경험(레시피, 실패, 선호) */
export async function backgroundKnowledge(problem: string): Promise<{ text: string; similar: number; experience: { recipes: number; failures: number; notes: number; total: number } }> {
  const [similar, ex] = await Promise.all([similarPastItems(problem), similarExperiences(problem)]);
  const refs = similar.length
    ? ['## 유사 기출 참고(출제 논리를 참고한다. 베끼지 않고 정답을 가정하지 않는다)', ...similar.map((s, i) => `${i + 1}. [${s.tags.join(', ') || '태그 없음'}] ${s.text.replace(/\s+/g, ' ').slice(0, 600)}`)].join('\n')
    : '';
  return { text: [CSAT_FORMAT_KNOWLEDGE, refs, experienceText(ex)].filter(Boolean).join('\n\n'), similar: similar.length, experience: { recipes: ex.recipes.length, failures: ex.failures.length, notes: ex.notes.length, total: ex.total } };
}
